import { redirect } from 'react-router-dom';

// GraphQL
import { client } from '../../../../gql/client';
import { SAVE_ACTION } from '../../../../gql/mutations/actions';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';
import { GET_SURVEILLANCE_ROUTE_BY_ID } from '../../../../gql/queries/surveillanceRoute';
import { removeNewActionStorage } from '../../../../utils/resetData';
import { actionTabsUnlockAll } from '../../../../state/actions/ui/actionTabs';
import { LINK_ORDER_TO_SEGMENT } from '../../../../gql/mutations/surveillanceRoute';
import { NEW_ORDER } from '../../../../gql/fragments/orders';
import { NEW_ACTION } from '../../../../gql/fragments/action';

// Types
import { OrderState } from '../../../../types/orders';

//Utils
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../utils/sessionStorage';
import { consumeFromSegment } from '../../../../utils/surveillanceRoute';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { store } from '../../../../state/configureStore';

import { prependActionToCache } from '../../../../gql/cacheHelpers';
import { esErrorTransitorioDeBackend } from '../../../../gql/retryableBackendCodes';
import { ActionBudgetItemRow } from './BudgetItems/types';
import { formActionNaming } from '../../../../components/forms/ActionForms/constants';
import { buildBasicActionSeed } from './basicActionSeed';
import { seedOfflineDraft } from './offlineDraft';
import { newCreationDraftId } from './creationDraft';
import {
  createPhaseDeadline,
  phaseScopedClient,
  isTransportFailure,
} from '../../../../offline/phase2Transport';
import {
  discardPartialActionDraft,
  readPreviousActionDraft,
  waitForPendingDraftDiscard,
} from '../../../../utils/discardPreviousPartialAction';

export const saveData = async (
  dataFormRaw: string | null,
  orderParentId: FormDataEntryValue | null,
  orderParentRoid: FormDataEntryValue | null,
  sessionStorageKey: string
) => {
  const dispatch = store.dispatch;

  if (dataFormRaw) {
    const decompressed = decompressSessionStorage(dataFormRaw);
    const dataForm = JSON.parse(decompressed);

    const cursorActions = getCursor(tableIds.VALIDATION_ACTIONS)(
      store.getState()
    );

    const creationDate = new Date().toISOString();
    const pkInitArray = dataForm.pkInit.replace(/ /g, '').split('+');
    const pkEndArray = dataForm.pkEnd.replace(/ /g, '').split('+');

    // Timer iteration 2026-04-15: in SR context, inherit operators/vehicles
    // from the parent route so the action is born fully staffed.
    const currentPath = window.location.pathname;
    const isSRContext = currentPath.startsWith('/surveillance-route/');
    let srUserIds: string[] | undefined;
    if (isSRContext) {
      const routeIdMatch = currentPath.match(/surveillance-route\/([^/]+)/);
      if (routeIdMatch?.[1]) {
        try {
          const cached = client.readQuery({
            query: GET_SURVEILLANCE_ROUTE_BY_ID,
            variables: { id: routeIdMatch[1] },
          });
          // Fix 2026-07-21: el campo se llama `userIDs` (patrón Action; ver
          // comentario en gql/queries/surveillanceRoute.ts) — `operatorIDs`
          // no existe en el schema y la herencia llevaba muerta desde el
          // rename (la actuación básica nunca nacía staffed).
          srUserIds = cached?.surveillanceRoute?.userIDs;
        } catch {
          // Cache miss — no operators to inherit
        }
      }
    }

    // V4 FR-9.5 AUTO (2026-05-07): si la creación de la Actuació viene de un
    // tramo cerrado con `pauseCause=action` (URL incluye `?fromSegment=<id>`),
    // vinculamos automáticamente el tram con la Actuació recién creada.
    // El tramo `Order.id` (no el `RouteSegment.id`) es el que linkea contra
    // la `Order.id` de la Actuació via `linkOrderToSegment`. El backend
    // resuelve el lookup desde el segmentId al orderId del segmento.
    let fromSegmentId: string | null = null;
    try {
      const url = new URL(window.location.href);
      fromSegmentId = url.searchParams.get('fromSegment');
    } catch {
      // SSR/no-window — silent
    }
    // I2 (QA IA 2026-07-13): un F5 a mitad de wizard pierde el ?fromSegment
    // de la URL (el router lo normaliza y el GET rebota al detalle) y el
    // auto-link se esfumaba en silencio. Fallback a la key persistida al
    // navegar al chain (rememberFromSegment) — consume-once y con scope del
    // RV actual, así un chain viejo nunca contamina otro recorrido.
    if (!fromSegmentId && isSRContext) {
      const routeIdMatch = currentPath.match(/\/surveillance-route\/([^/]+)/);
      if (routeIdMatch) {
        fromSegmentId = consumeFromSegment(routeIdMatch[1]);
      }
    }

    // Bloque C-5: SAVE_ACTION (atómico) sustituye CREATE_NEW_ORDER + CREATE_NEW_ACTION
    // + UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS por una transacción Prisma única.
    // Backend infiere indexExpedition/indexType del parentOrderId y populiza la
    // entidad doble Order ↔ Action atómicamente — elimina deadlock P2034.
    // El clientOpId se genera ANTES de llamar: si el create se aborta por el
    // plazo y caemos al modo sin red, viaja en el borrador y el replay lo
    // reutiliza — un create que sí llegó devuelve la actuación ya creada en
    // vez de duplicarla (spec 2026-09-08-fase2-encolada § 2 D3/D6).
    const createClientOpId = crypto.randomUUID();
    const deadline = createPhaseDeadline();
    const phaseClient = phaseScopedClient(client, deadline);
    let dataSaveAction;
    try {
      dataSaveAction = await phaseClient.mutate({
        mutation: SAVE_ACTION,
        variables: {
          input: {
            // Idempotencia online: UUID estable por operación lógica, generado
            // ANTES de llamar (ver `createClientOpId`).
            clientOpId: createClientOpId,
            parentOrderId: orderParentId as string,
            state: OrderState.Partial,
            creationDate,
            pkInitKm: Number(pkInitArray[0]),
            pkInitMeter: Number(pkInitArray[1]),
            pkEndKm: Number(pkEndArray[0]),
            pkEndMeter: Number(pkEndArray[1]),
            track: dataForm.track,
            direction: dataForm?.direction,
            margin: dataForm?.margin,
            lane: dataForm.lane,
            concessionId: dataForm.concession,
            roadId: dataForm.road,
            capitolId: dataForm.capitol,
            subCapitolId: dataForm.subCapitol,
            ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
            // C-11.12 (main): el wizard NewAction tiene el dropdown "Elemento"
            // (`actionElementOrder`) en el paso 1 (información básica), no en
            // Edition. Mandarlo aquí para que `Order.elementOrderIDs` se
            // persista al crear la action en estado partial. Antes este campo
            // se perdía y solo se intentaba (sin éxito) en el paso 2.
            ...(dataForm.actionElementOrder && {
              elementOrderId: dataForm.actionElementOrder,
            }),
            description: dataForm.description,
            notes: dataForm.notes,
            activityId: dataForm?.actionActivity,
            // Operario que registra (persona real). Obligatorio al crear en la UI;
            // el backend además lo exige para rol Tablet.
            ...(dataForm.registeredByUserId && {
              registeredByUserId: dataForm.registeredByUserId,
            }),
            // SR (FR-9.3): si el flujo nace dentro de un Recorregut de Vigilància,
            // heredamos los operarios del padre RV para que la actuación nazca
            // staffed. Si no estamos en SR context, `srUserIds` queda undefined
            // y no se incluye en el input.
            ...(srUserIds?.length && { userIds: srUserIds }),
          },
        },
        update(cache, { data: { saveAction } }) {
          // Cache prepend + childExpeditionOrderIDs push + multimedia invalidate
          // (helper reutilizable; mismo path que ejecuta el sync offline).
          prependActionToCache(
            cache,
            saveAction,
            orderParentId ? String(orderParentId) : undefined
          );
          // Side effect online: reset cursor para que el usuario vea la nueva
          // action arriba al volver a la lista. NO se replica en sync offline.
          if (cursorActions?.cursor !== undefined) {
            dispatch(
              setCursor(tableIds.VALIDATION_ACTIONS, {
                ...cursorActions,
                take: takeBase,
                cursor: '',
                orderBy: 'desc',
              })
            );
          }
        },
      });
    } catch (err) {
      deadline.clear();
      if (!isTransportFailure(err)) throw err;
      // Red lenta o cortada: el asistente sigue como sin red (mismo camino que
      // el sondeo negativo de la página). La fase 2 encolará la creación con
      // este clientOpId.
      seedOfflineDraft(
        {
          ...dataForm,
          orderParentId,
          orderParentRoid,
          ...(fromSegmentId ? { fromSegmentId } : {}),
        },
        { createClientOpId }
      );
      return redirect(
        isSRContext ? 'details/location' : 'details/operators-and-vehicles'
      );
    }

    const createdOrder = dataSaveAction.data?.saveAction?.order;
    const createdAction = dataSaveAction.data?.saveAction?.action;

    // Refetch order data — solo calienta la caché: si el plazo vence aquí, la
    // actuación ya existe y el asistente sigue.
    if (createdOrder?.id) {
      try {
        await phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ORDER_BY_ID,
          variables: { id: createdOrder.id },
        });
      } catch (refreshErr) {
        if (!isTransportFailure(refreshErr)) throw refreshErr;
      }
    }
    deadline.clear();

    // Bugfix P15 Fase 1 (2026-07-08): `linkOrderToSegment`'s `orderId` es el
    // id de la **Order** de la Actuació (el backend hace
    // `prisma.order.findUnique({ where: { id: orderId } })` — ver
    // `route-segment.service.ts#linkOrder`), no el id de la ENTITY `Action`.
    // Este bloque pasaba `createdAction.id` (Action, colección distinta de
    // Order) → el lookup del backend siempre fallaba con `NotFoundException`,
    // silenciada por el catch de abajo — el auto-link NUNCA se creaba en la
    // práctica. Corregido a `createdOrder.id`, coherente con el comentario de
    // arriba y con cómo `RouteChildOrders` invoca la misma mutation (Fase 1).
    if (fromSegmentId && isSRContext && createdOrder?.id) {
      try {
        await client.mutate({
          mutation: LINK_ORDER_TO_SEGMENT,
          variables: {
            input: {
              segmentId: fromSegmentId,
              orderId: createdOrder.id,
            },
          },
        });
      } catch (linkErr) {
        // Vinculación no-crítica: la Actuació se creó OK, solo el cross-link
        // falló. El usuario puede vincular manualmente vía RelateSegmentModal.
        console.warn(
          '[SR-AutoLink] linkOrderToSegment failed, continuing without auto-link:',
          linkErr
        );
      }
    }

    const dataToSave = {
      ...dataForm,
      // El borrador sigue siendo el que empezó su puerta (`pass-data`): mismo
      // `draftId`, así el reclamo de las pestañas no descarta nada. Una sesión
      // de antes de este cambio no lo trae y recibe uno aquí (spec 2026-11-02
      // wizard-draft-ownership § 6).
      draftId: dataForm.draftId || newCreationDraftId(),
      orderId: createdOrder?.id,
      actionId: createdAction?.id,
      // Persistir fromSegment en sessionStorage por si el flow se reanuda
      // (e.g. tras F5 mid-wizard). El backend reusa el link existente
      // idempotentemente — re-llamar `linkOrderToSegment` con la misma pareja
      // no crea duplicado.
      ...(fromSegmentId ? { fromSegmentId } : {}),
    };

    const jsonString = JSON.stringify(dataToSave);
    const compressed = compressSessionStorage(jsonString);
    sessionStorage.setItem(sessionStorageKey, compressed);
  }

  // Timer iteration 2026-04-15: in SR context the operators tab is hidden,
  // so redirect to the first visible tab (location) instead.
  const path = window.location.pathname;
  if (path.startsWith('/surveillance-route/')) {
    return redirect('details/location');
  }
  return redirect('details/operators-and-vehicles');
};

/**
 * Cargar partidas presupuestarias desde sessionStorage
 */
export const loadActionBudgetItems = (
  actionId: string
): ActionBudgetItemRow[] => {
  try {
    const stored = sessionStorage.getItem(
      formActionNaming.NEW_BUDGET_ITEMS_FORM
    );

    if (stored) {
      return JSON.parse(stored);
    }

    return [];
  } catch (error) {
    console.error('Error loading action budget items from session:', error);
    return [];
  }
};

/**
 * Limpiar partidas presupuestarias del sessionStorage
 */
export const clearActionBudgetItems = (actionId: string): void => {
  try {
    sessionStorage.removeItem(formActionNaming.NEW_BUDGET_ITEMS_FORM);
  } catch (error) {
    console.error('Error clearing action budget items from session:', error);
  }
};

/**
 * P9 auto-create (decisión usuario 2026-07-21): en el Recorregut de
 * Vigilància, "Crear Actuació" (botón de la card o cierre de tramo con
 * causa Actuació) NO pasa por el form de Descripción — el sistema crea la
 * Actuació básica CON los datos heredados y aterriza directo en el wizard
 * reducido (`details/location`, tabs Operaris/Maquinària ocultas FR-9.3).
 *
 * Herencia: PKs/sentido/carril del TRAMO si venimos de `?fromSegment`
 * (ahí ocurrió la incidencia), si no los PKs del recorrido; operarios del
 * RV (`userIDs` — la actuación nace staffed, FR-9.3); concesión/carretera
 * del RV; descripción = detalle de la causa del StopCauseModal si lo hay.
 * La classification la hereda el BACKEND del padre (fallback Operació).
 * Todo lo demás se refina en el wizard (por eso P9 aterriza en Ubicació).
 */
/** Plazo de la relectura de la básica tras vincularla al tramo. */
const POST_LINK_REFRESH_MS = 3_000;

/**
 * Esperas entre reintentos del alta de la básica ante un aborto transitorio
 * del back (QA T3.4+a / T3.5+a, 15-oct). El `errorLink` ya reintenta UNA vez,
 * pero al instante: si la otra escritura de la misma Order del recorrido
 * sigue en vuelo, vuelve a chocar (P2034) y la básica no se creaba. Las
 * mismas esperas que el descarte del borrador anterior.
 */
const SAVE_RETRY_DELAYS_MS = [400, 1200];

const waitMs = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const createBasicAction = async (params: {
  routeId: string;
  orderParentId: string;
  orderParentRoid: string;
  fromSegmentId?: string | null;
  description?: string | null;
}) => {
  const { orderParentId, fromSegmentId } = params;
  const dispatch = store.dispatch;

  // El borrador que dejara a medias el asistente anterior de esta pestaña se
  // descarta, como en «Crear Actuación». Sin esto, el cronómetro y la pill
  // del tramo olvidaban el puntero SIN borrar la parcial: quedaba huérfana,
  // sin capítulo, y bloqueaba validar el recorrido (diagnóstico 2026-10-08
  // § 7). Aquí solo se CAPTURA el puntero (síncrono), antes de vaciarlo; el
  // borrado va al final, cuando la básica nueva ya existe.
  const previousDraft = readPreviousActionDraft();

  // Contrato del flujo normal: empezar wizard = storage por-tab LIMPIO, y
  // navegación libre entre pestañas (la acción YA existe con todos los datos).
  removeNewActionStorage();
  dispatch(actionTabsUnlockAll());

  // La MISMA semilla que el camino sin red (`seedBasicActionOffline`).
  const { seed, input } = await buildBasicActionSeed({ ...params, offline: false });

  const cursorActions = getCursor(tableIds.VALIDATION_ACTIONS)(store.getState());

  // Mismo esquema que la fase 1 de la normal (D3/D6): clientOpId antes de
  // llamar y, si el create no llega, la semilla sin red lo lleva.
  const createClientOpId = crypto.randomUUID();
  const deadline = createPhaseDeadline();
  const phaseClient = phaseScopedClient(client, deadline);
  // El borrado que dejara en vuelo una cadena ANTERIOR (lanzada sin esperar,
  // abajo) escribe la misma Order del recorrido: crear a la vez = P2034. Se
  // espera, con tope (remate del 15-oct, QA T3.4+a / T3.5+a).
  await waitForPendingDraftDiscard();

  let dataSaveAction;
  // Un aborto transitorio (P2034 y compañía) se reintenta con espera: es
  // SEGURO, el back deshace la transacción entera y el `clientOpId` es el
  // mismo en todos los intentos (no puede duplicarla).
  for (let attempt = 0; ; attempt++) {
    try {
      dataSaveAction = await phaseClient.mutate({
        mutation: SAVE_ACTION,
        variables: { input: { clientOpId: createClientOpId, ...input } },
        update(cache, { data: { saveAction } }) {
          prependActionToCache(cache, saveAction, orderParentId);
          if (cursorActions?.cursor !== undefined) {
            dispatch(
              setCursor(tableIds.VALIDATION_ACTIONS, {
                ...cursorActions,
                take: takeBase,
                cursor: '',
                orderBy: 'desc',
              })
            );
          }
        },
      });
      break;
    } catch (err) {
      if (
        attempt < SAVE_RETRY_DELAYS_MS.length &&
        esErrorTransitorioDeBackend(err)
      ) {
        await waitMs(SAVE_RETRY_DELAYS_MS[attempt]);
        continue;
      }
      deadline.clear();
      if (!isTransportFailure(err)) throw err;
      return seedBasicActionOffline({ ...params, createClientOpId });
    }
  }

  const createdOrder = dataSaveAction.data?.saveAction?.order;
  const createdAction = dataSaveAction.data?.saveAction?.action;

  if (createdOrder?.id) {
    try {
      await phaseClient.query({
        fetchPolicy: 'network-only',
        query: GET_ORDER_BY_ID,
        variables: { id: createdOrder.id },
      });
    } catch (refreshErr) {
      if (!isTransportFailure(refreshErr)) throw refreshErr;
    }
  }
  deadline.clear();

  // Auto-link tramo ↔ actuación (FR-9.5 AUTO). `orderId` = Order de la
  // actuación (ver nota Bugfix P15 Fase 1 en saveData de arriba).
  if (fromSegmentId && createdOrder?.id) {
    try {
      await client.mutate({
        mutation: LINK_ORDER_TO_SEGMENT,
        variables: { input: { segmentId: fromSegmentId, orderId: createdOrder.id } },
      });
      // Vincular es lo que le corrige el horario en el servidor (inicio = fin
      // del tramo; fin = inicio del siguiente si ya existe): sin releer el
      // Order, «Datos Act» enseñaba la hora de alta y el fin vacío (banco en
      // beta, 2026-10-08). Con su propio plazo, CORTO: es solo para enseñar la
      // hora, y con mala cobertura el de la fase (25 s) era spinner de más en
      // cada básica. Si no llega, «Datos Act» enseña lo de la caché.
      const refreshDeadline = createPhaseDeadline(POST_LINK_REFRESH_MS);
      try {
        await phaseScopedClient(client, refreshDeadline).query({
          fetchPolicy: 'network-only',
          query: GET_ORDER_BY_ID,
          variables: { id: createdOrder.id },
        });
      } catch {
        // Sin relectura: el horario se ve en la validación.
      } finally {
        refreshDeadline.clear();
      }
    } catch (linkErr) {
      console.warn(
        '[SR-AutoLink] linkOrderToSegment failed, continuing without auto-link:',
        linkErr
      );
    }
  }

  // `draftId` NUEVO: esta es una puerta que EMPIEZA una creación. Es la `key`
  // de la pantalla del asistente: el cronómetro puede crear esta básica
  // estando en Ubicación de la anterior y redirigir a la MISMA URL; sin un id
  // distinto, React reutilizaba el formulario montado y la básica nueva se
  // guardaba con el PK, la carretera y el sentido de la anterior (auditoría
  // de creación 2.1, M2 del spec 2026-11-02 wizard-draft-ownership).
  sessionStorage.setItem(
    formActionNaming.NEW_ACTION_DATA,
    compressSessionStorage(
      JSON.stringify({
        ...seed,
        draftId: newCreationDraftId(),
        orderId: createdOrder?.id,
        actionId: createdAction?.id,
      })
    )
  );

  // 🔴 El borrador anterior se descarta DESPUÉS de crear la básica nueva, no
  // a la vez: en paralelo, el borrado y el `SAVE_ACTION` escriben la misma
  // Order del recorrido y el backend aborta el borrado («Conflicto con otra
  // operación simultánea», P2034). Medido en beta el 2026-10-09 (Su.0000620 y
  // Su.0000615): el borrador quedaba huérfano. SIN `await`: entrar al
  // asistente no espera a la red (puerta del cronómetro, en carretera). Sin
  // red no se borra nada; un choque transitorio se reintenta. No va suelto:
  // queda guardado y la SIGUIENTE cadena lo espera antes de crear su básica
  // (`waitForPendingDraftDiscard`, arriba).
  void discardPartialActionDraft(previousDraft);

  return redirect('details/location');
};

/**
 * La básica SIN cobertura (2026-09-02): misma semilla, ninguna mutation.
 *
 * Antes el router action degradaba al formulario de la actuación NORMAL, y lo
 * que salía de ahí no era una básica (sin `containerType`), no se vinculaba al
 * tramo y al guardar te mandaba al detalle de OT. Ahora se entra al wizard
 * reducido igual que con red; la fase 2 encola la operación compound (como ya
 * hace la normal) y `syncAction` crea la actuación y la vincula al sincronizar.
 */
export const seedBasicActionOffline = async (params: {
  routeId: string;
  orderParentId: string;
  orderParentRoid: string;
  fromSegmentId?: string | null;
  description?: string | null;
  /** El del intento con red que se abortó (D3): el replay lo reutiliza. */
  createClientOpId?: string;
}) => {
  removeNewActionStorage();
  store.dispatch(actionTabsUnlockAll());

  const { seed } = await buildBasicActionSeed({ ...params, offline: true });

  sessionStorage.setItem(
    formActionNaming.NEW_ACTION_DATA,
    compressSessionStorage(
      JSON.stringify({
        ...seed,
        // Puerta que empieza una creación: `draftId` nuevo (ver
        // `createBasicAction`). Dos básicas seguidas sin red no comparten
        // pantalla ni copias.
        draftId: newCreationDraftId(),
        _isOffline: true,
        ...(params.createClientOpId ? { createClientOpId: params.createClientOpId } : {}),
      })
    )
  );

  return redirect('details/location');
};
